// ============================================================================
// Layout del storefront (Parte 2): cabecera por variante del editor, barra de
// busqueda, navegacion, drawers moviles, footer con branding y aviso de tienda
// cerrada/abierta. Funciones puras de construccion de HTML: la conexion de
// eventos vive en main.js (conectarLayout).
// ============================================================================

import { formatearMoneda } from '@plataforma/utils';

import { obtenerLineasCarrito } from './carrito.js';
import { escaparHtml, imagenPrincipal } from './componentes.js';
import { debeMostrarHorario, formatearHorario } from './horario.js';
import { iconoLucide } from './iconos.js';
import { iconoRedSocial } from './redes-sociales.js';

export function marcaHtml(tienda, { blanco = false, paraFooter = false } = {}) {
  const logo =
    blanco || paraFooter
      ? tienda.perfil.logo_blanco_url || tienda.perfil.logo_url
      : tienda.perfil.logo_url;

  if (logo) {
    return `<img src="${escaparHtml(logo)}" alt="${escaparHtml(tienda.empresa.nombre)}" decoding="async" />`;
  }

  return `<span class="sp-marca__fallback" aria-hidden="true">${escaparHtml(
    tienda.empresa.nombre.slice(0, 1),
  )}</span>`;
}

function enlaceWhatsAppSeguro(tienda) {
  const telefono = String(tienda.perfil.whatsapp ?? '').replace(/\D/g, '');
  if (!telefono) return '';
  return `https://wa.me/${telefono}`;
}

export function enlacesRedes(tienda) {
  return [
    ['whatsapp', enlaceWhatsAppSeguro(tienda), 'WhatsApp'],
    ['instagram', tienda.perfil.instagram, 'Instagram'],
    ['facebook', tienda.perfil.facebook, 'Facebook'],
    ['tiktok', tienda.perfil.tiktok, 'TikTok'],
    ['youtube', tienda.perfil.youtube, 'YouTube'],
  ].filter(([, url]) => Boolean(url));
}

export function navHtml() {
  return `
    <a href="/">${iconoLucide('Home')}<span>Inicio</span></a>
    <a href="/catalogo">${iconoLucide('Grid3X3')}<span>Catálogo</span></a>
    <a href="/contacto">${iconoLucide('Phone')}<span>Contacto</span></a>
  `;
}

export function navCategoriasHtml(tienda) {
  const categorias = (tienda.categorias ?? []).slice(0, 6);
  return `
    <a href="/catalogo">Todos</a>
    ${categorias
      .map(
        (categoria) =>
          `<a href="/catalogo?categoria=${escaparHtml(categoria.slug)}">${escaparHtml(categoria.nombre)}</a>`,
      )
      .join('')}
  `;
}

export function buscadorHtml() {
  return `
    <form class="sp-header__busqueda" data-sp-busqueda role="search">
      <input type="search" name="q" placeholder="Buscar productos…" aria-label="Buscar productos" autocomplete="off" />
      <button type="submit" aria-label="Buscar">${iconoLucide('Search')}</button>
    </form>
  `;
}

export function buscadorExpandibleHtml() {
  return `
    <div class="sp-header__busqueda-expandible" data-sp-busqueda-expandible>
      <button type="button" class="sp-header__busqueda-trigger" data-sp-busqueda-abrir aria-label="Abrir búsqueda" aria-expanded="false" aria-controls="sp-busqueda-panel">
        ${iconoLucide('Search')}
      </button>
      <div class="sp-header__busqueda-panel" id="sp-busqueda-panel" role="search" hidden>
        <form class="sp-header__busqueda" data-sp-busqueda>
          <input type="search" name="q" placeholder="Buscar productos…" aria-label="Buscar productos" autocomplete="off" />
          <button type="submit" aria-label="Buscar">${iconoLucide('Search')}</button>
          <button type="button" class="sp-header__busqueda-cerrar" data-sp-busqueda-cerrar aria-label="Cerrar búsqueda">${iconoLucide('X')}</button>
        </form>
      </div>
    </div>
  `;
}

function accionesCarritoHtml() {
  return `
    <a class="sp-carrito-link" href="/carrito" data-sp-carrito-abrir aria-label="Abrir carrito">
      ${iconoLucide('ShoppingCart')}
      <span class="sp-contador" id="sp-contador-carrito">0</span>
    </a>
  `;
}

function marcaConMeta(tienda, blanco, opciones = {}) {
  const { grande = false, variante = '' } = opciones;
  const claseGrande = grande ? ' sp-marca--grande' : '';
  return `
    <a class="sp-marca${claseGrande}" href="/" aria-label="${escaparHtml(tienda.empresa.nombre)}" data-part="logo" data-variante="${variante}">
      ${marcaHtml(tienda, { blanco })}
      <span>${escaparHtml(tienda.empresa.nombre)}</span>
    </a>
  `;
}

function botonMenuHtml() {
  return `
    <button class="sp-icon-button sp-header__menu" type="button" data-sp-menu-abrir aria-label="Abrir menú">
      ${iconoLucide('Menu')}
    </button>
  `;
}

/* ============================================================================
   VARIANTE: CLÁSICO
   Tienda sólida, equilibrada, confiable. Composición simétrica en 3 zonas
   (1fr auto 1fr) donde el logo vive ópticamente centrado: navegación ligera
   (solo texto, sin iconos) a la izquierda y acciones discretas (iconos) a la
   derecha con el mismo peso visual.
   Desktop: [☰ + NAV texto] [LOGO centrado] [carrito]
   Mobile:  [☰] [LOGO centrado] [carrito]  (whatsapp vive en el drawer)
   ============================================================================ */
function headerClasico(tienda, estado) {
  const fondoSolido = estado.header.caracteristicas.fondo_solido;
  const blanco = !fondoSolido;
  return `
    <header class="sp-header sp-header--clasico" data-sp-header data-variante="clasico" data-fondo-solido="${fondoSolido}">
      <div class="sp-contenedor sp-header__contenido sp-header__contenido--clasico">
        <div class="sp-header__izquierda">
          ${botonMenuHtml()}
          <nav class="sp-nav sp-nav--clasico" aria-label="Principal">
            ${navHtml()}
          </nav>
        </div>
        <div class="sp-header__centro">
          ${marcaConMeta(tienda, blanco, { variante: 'clasico' })}
        </div>
        <div class="sp-header__acciones">
          ${accionesCarritoHtml()}
        </div>
      </div>
    </header>
  `;
}

/* ============================================================================
   VARIANTE: ECOMMERCE
   Tienda orientada a compra y catálogo, elegante y funcional. El buscador vive
   integrado en la cabecera (centro, proporcionado, nunca gigante) y las
   categorías se apoyan en una barra serena bajo la cabecera.
   Desktop: [MARCA] [BUSCADOR integrado] [ACCIONES: carrito + ☰]
   Mobile:  [☰] [MARCA] [BUSCADOR-icono + carrito]  — el buscador NO domina la
            pantalla: se expande como un panel ligero bajo la fila de iconos.
   ============================================================================ */
function headerEcommerce(tienda, estado) {
  const fondoSolido = estado.header.caracteristicas.fondo_solido;
  const blanco = !fondoSolido;
  const conBuscador = estado.header.caracteristicas.buscador;
  return `
    <header class="sp-header sp-header--ecommerce" data-sp-header data-variante="ecommerce" data-fondo-solido="${fondoSolido}">
      <div class="sp-contenedor sp-header__contenido sp-header__contenido--ecommerce">
        <div class="sp-header__marca-ecommerce">
          ${botonMenuHtml()}
          ${marcaConMeta(tienda, blanco, { variante: 'ecommerce' })}
        </div>
        ${conBuscador ? `<div class="sp-header__centro"><div class="sp-header__busqueda-wrap" data-sp-busqueda-wrap>${buscadorHtml()}</div></div>` : ''}
        <div class="sp-header__acciones sp-header__acciones--ecommerce">
          ${accionesCarritoHtml()}
          ${conBuscador ? buscadorExpandibleHtml() : ''}
        </div>
      </div>
      ${conBuscador ? `<nav class="sp-header__barra-categorias" aria-label="Categorías"><div class="sp-contenedor">${navCategoriasHtml(tienda)}</div></nav>` : ''}
    </header>
  `;
}

/* ============================================================================
   VARIANTE: PREMIUM
   Exclusivo, editorial, sofisticado. Mucho aire, marca protagonista sin ser
   enorme, navegación discreta centrada y acciones minimalistas en el extremo
   opuesto. Compone bien incluso transparente sobre el hero.
   Desktop: [MARCA grande] [NAV centrado con subrayado] [ACCIONES discretas:
            carrito]
   Mobile:  [MARCA] [ACCIONES: carrito + ☰ a la DERECHA]
   ============================================================================ */
function headerPremium(tienda, estado) {
  const fondoSolido = estado.header.caracteristicas.fondo_solido;
  const transparente = estado.header.caracteristicas.transparente;
  const blanco = !fondoSolido;
  const claseExtra = transparente ? ' sp-header--transparente' : '';
  return `
    <header class="sp-header sp-header--premium${claseExtra}" data-sp-header data-variante="premium" data-fondo-solido="${fondoSolido}" data-transparente="${transparente}" data-scroll="false">
      <div class="sp-contenedor sp-header__contenido sp-header__contenido--premium">
        <div class="sp-header__marca-premium">
          ${marcaConMeta(tienda, blanco, { grande: true, variante: 'premium' })}
        </div>
        <nav class="sp-nav sp-nav--premium" aria-label="Principal">
          ${navHtml()}
        </nav>
        <div class="sp-header__acciones sp-header__acciones--premium">
          ${accionesCarritoHtml()}
          ${botonMenuHtml()}
        </div>
      </div>
    </header>
  `;
}

/* ============================================================================
   VARIANTE: MINIMAL
   Extremadamente limpio: marca protagonista con mucho aire y solo acciones
   esenciales. No es "menos elementos" a secas: cada pieza está finamente
   proporcionada y la composición móvil es independiente del desktop.
   Desktop: [MARCA protagonista] [ACCIONES: carrito + ☰]
   Mobile:  [MARCA] [ACCIONES: carrito + ☰ a la DERECHA]
   ============================================================================ */
function headerMinimal(tienda, estado) {
  const fondoSolido = estado.header.caracteristicas.fondo_solido;
  const blanco = !fondoSolido;
  return `
    <header class="sp-header sp-header--minimal" data-sp-header data-variante="minimal" data-fondo-solido="${fondoSolido}">
      <div class="sp-contenedor sp-header__contenido sp-header__contenido--minimal">
        <div class="sp-header__marca-minimal">
          ${marcaConMeta(tienda, blanco, { variante: 'minimal' })}
        </div>
        <div class="sp-header__acciones sp-header__acciones--minimal">
          ${accionesCarritoHtml()}
          ${botonMenuHtml()}
        </div>
      </div>
    </header>
  `;
}

/* ============================================================================
   VARIANTE: MODERNO
   Audaz, contemporáneo, alto contraste. Doble plano: una franja utilitaria
   superior (datos de contacto condensados) y una cabecera principal con
   marca en negrita, navegación completa con icono+texto y acciones. Borde
   inferior marcado que estructura la página.
   Desktop: [franja util] [MARCA] [NAV icono+texto] [carrito + ☰]
   Mobile:  [franja util compacta] [☰ + MARCA + carrito]
   ============================================================================ */
function headerModerno(tienda, estado) {
  const fondoSolido = estado.header.caracteristicas.fondo_solido;
  const blanco = !fondoSolido;
  const tel = String(tienda.perfil.whatsapp ?? '').replace(/\D/g, '');
  const direccion = tienda.perfil.direccion;
  return `
    <header class="sp-header sp-header--moderno" data-sp-header data-variante="moderno" data-fondo-solido="${fondoSolido}">
      <div class="sp-header__util">
        <div class="sp-contenedor sp-header__util-contenido">
          <span class="sp-header__util-item">
            ${iconoLucide('MapPin')}<span>${escaparHtml(direccion || 'Tu dirección')}</span>
          </span>
          ${
            tel
              ? `<a class="sp-header__util-item" href="https://wa.me/${tel}" target="_blank" rel="noreferrer">${iconoLucide('Phone')}<span>${escaparHtml(String(tienda.perfil.whatsapp))}</span></a>`
              : ''
          }
        </div>
      </div>
      <div class="sp-contenedor sp-header__contenido sp-header__contenido--moderno">
        <div class="sp-header__marca-moderno">
          ${marcaConMeta(tienda, blanco, { variante: 'moderno' })}
        </div>
        <nav class="sp-nav sp-nav--moderno" aria-label="Principal">
          ${navHtml()}
        </nav>
        <div class="sp-header__acciones sp-header__acciones--moderno">
          ${accionesCarritoHtml()}
          ${botonMenuHtml()}
        </div>
      </div>
    </header>
  `;
}

/* ============================================================================
   VARIANTE: MARKETPLACE
   Estética de gran mercado / multi-vendedor: capas claras y escaneables.
   Franja superior oscura (marca pequeña + utilidades), fila principal con
   búsqueda amplia y acciones, y una barra gruesa de categorías (más carga)
   debajo. Busca/compra orientado, muy distinto del ecommerce de una fila.
   Desktop: [franja oscura] [MARCA] [BUSCADOR amplio] [carrito + ☰] [CATS barra]
   Mobile:  [☰ + MARCA + carrito] [BUSCADOR debajo (amplio)] 
   ============================================================================ */
function headerMarketplace(tienda, estado) {
  const fondoSolido = estado.header.caracteristicas.fondo_solido;
  const blanco = !fondoSolido;
  const conBuscador = estado.header.caracteristicas.buscador;
  return `
    <header class="sp-header sp-header--marketplace" data-sp-header data-variante="marketplace" data-fondo-solido="${fondoSolido}">
      <div class="sp-header__util sp-header__util--oscuro">
        <div class="sp-contenedor sp-header__util-contenido">
          <span class="sp-header__util-item sp-header__util-item--claro">
            ${iconoLucide('BadgeCheck')}<span>${escaparHtml(tienda.empresa.nombre)}</span>
          </span>
          <span class="sp-header__util-item sp-header__util-item--claro">
            ${iconoLucide('Truck')}<span>Envíos seguros</span>
          </span>
        </div>
      </div>
      <div class="sp-contenedor sp-header__contenido sp-header__contenido--marketplace">
        <div class="sp-header__marca-marketplace">
          ${marcaConMeta(tienda, blanco, { variante: 'marketplace' })}
        </div>
        ${conBuscador ? `<div class="sp-header__centro sp-header__centro--marketplace"><div class="sp-header__busqueda-wrap" data-sp-busqueda-wrap>${buscadorHtml()}</div></div>` : ''}
        <div class="sp-header__acciones sp-header__acciones--marketplace">
          ${accionesCarritoHtml()}
          ${botonMenuHtml()}
        </div>
      </div>
      <nav class="sp-header__barra-categorias sp-header__barra-categorias--marketplace" aria-label="Categorías">
        <div class="sp-contenedor">${navCategoriasHtml(tienda)}</div>
      </nav>
    </header>
  `;
}

/* ============================================================================
   VARIANTE: EDITORIAL
   Editorial / revista: alto, tipográfico, con doble línea de guía. Marca y
   navegación en diagonales separadas con mucha altura, navegación en
   versalitas pequeñas sobre una segunda línea, hairlines finas.
   Desktop: [MARCA grande]  /  [NAV versalitas — segunda línea full-width]
            [carrito + ☰]
   Mobile:  [☰ + MARCA + carrito]  (nav → drawer)
   ============================================================================ */
function headerEditorial(tienda, estado) {
  const fondoSolido = estado.header.caracteristicas.fondo_solido;
  const blanco = !fondoSolido;
  return `
    <header class="sp-header sp-header--editorial" data-sp-header data-variante="editorial" data-fondo-solido="${fondoSolido}">
      <div class="sp-contenedor sp-header__contenido sp-header__contenido--editorial">
        <div class="sp-header__zona-editorial sp-header__zona-editorial--superior">
          <div class="sp-header__marca-editorial">
            ${marcaConMeta(tienda, blanco, { variante: 'editorial' })}
          </div>
          <div class="sp-header__acciones sp-header__acciones--editorial">
            ${accionesCarritoHtml()}
            ${botonMenuHtml()}
          </div>
        </div>
        <nav class="sp-nav sp-nav--editorial" aria-label="Principal">
          ${navHtml()}
        </nav>
      </div>
    </header>
  `;
}

/* ============================================================================
   VARIANTE: FLOTANTE
   Flotante / cristal: transparente sobre el hero, con el contenido encerrado
   en una pastilla redondeada que "flota" con sombra y blur, separada del
   borde superior. Sticky y elegante. Sin bajar ni perder la marca.
   Desktop: [ (pastilla) MARCA | NAV centrado | carrito + ☰ (pastilla) ]
   Mobile:  [ pastilla: ☰ + MARCA + carrito ]
   ============================================================================ */
function headerFlotante(tienda, estado) {
  const fondoSolido = estado.header.caracteristicas.fondo_solido;
  const blanco = !fondoSolido;
  return `
    <header class="sp-header sp-header--flotante" data-sp-header data-variante="flotante" data-fondo-solido="${fondoSolido}" data-transparente="true">
      <div class="sp-contenedor sp-header__contenido sp-header__contenido--flotante">
        <div class="sp-header__pastilla">
          <div class="sp-header__marca-flotante">
            ${marcaConMeta(tienda, blanco, { variante: 'flotante' })}
          </div>
          <nav class="sp-nav sp-nav--flotante" aria-label="Principal">
            ${navHtml()}
          </nav>
          <div class="sp-header__acciones sp-header__acciones--flotante">
            ${accionesCarritoHtml()}
            ${botonMenuHtml()}
          </div>
        </div>
      </div>
    </header>
  `;
}

export function headerHtml(tienda, estado) {
  const porVariante = {
    clasico: headerClasico,
    ecommerce: headerEcommerce,
    premium: headerPremium,
    minimal: headerMinimal,
    moderno: headerModerno,
    marketplace: headerMarketplace,
    editorial: headerEditorial,
    flotante: headerFlotante,
  };
  const render = porVariante[estado.header.id] ?? headerClasico;
  return render(tienda, estado);
}

export function drawerMenuHtml(tienda, estado) {
  const conBuscador = estado.header.caracteristicas.buscador;
  return `
    <aside class="sp-drawer sp-drawer--menu" data-sp-menu aria-hidden="true" role="dialog" aria-modal="true" aria-label="Menú">
      <div class="sp-drawer__cabecera">
        <a class="sp-marca" href="/">
          ${marcaHtml(tienda)}
          <span>${escaparHtml(tienda.empresa.nombre)}</span>
        </a>
        <button class="sp-icon-button" type="button" data-sp-menu-cerrar aria-label="Cerrar menú">
          ${iconoLucide('X')}
        </button>
      </div>
      ${conBuscador ? `<div class="sp-drawer__busqueda">${buscadorHtml()}</div>` : ''}
      <nav class="sp-drawer__nav" aria-label="Menú móvil">
        ${navHtml()}
        <a href="/carrito">${iconoLucide('ShoppingCart')}<span>Carrito</span></a>
      </nav>
      ${
        enlacesRedes(tienda).length
          ? `<div class="sp-drawer__redes sp-redes sp-redes--iconos">${enlacesRedes(tienda)
              .map(
                ([clave, url, nombre]) =>
                  `<a href="${escaparHtml(url)}" target="_blank" rel="noreferrer" aria-label="${escaparHtml(nombre)}">${iconoRedSocial(clave, { label: nombre })}</a>`,
              )
              .join('')}</div>`
          : ''
      }
    </aside>
  `;
}

export function drawerCarritoHtml(tienda) {
  return `
    <aside class="sp-drawer sp-drawer--carrito" data-sp-carrito aria-hidden="true" role="dialog" aria-modal="true" aria-label="Carrito">
      <div class="sp-drawer__cabecera">
        <strong>Carrito</strong>
        <button class="sp-icon-button" type="button" data-sp-carrito-cerrar aria-label="Cerrar carrito">
          ${iconoLucide('X')}
        </button>
      </div>
      <div class="sp-mini-carrito" data-sp-mini-carrito>
        ${miniCarritoHtml(tienda)}
      </div>
    </aside>
  `;
}

export function miniCarritoHtml(tienda) {
  const moneda = tienda.empresa?.moneda ?? 'PEN';
  const lineas = obtenerLineasCarrito(tienda).slice(0, 4);

  if (!lineas.length) {
    return `
      <div class="sp-mini-carrito__vacio">
        ${iconoLucide('ShoppingBag')}
        <p>Tu carrito está vacío.</p>
        <a class="sp-boton sp-boton--primario" href="/catalogo">Ver catálogo</a>
      </div>
    `;
  }

  return `
    <div class="sp-mini-carrito__lineas">
      ${lineas
        .map((linea) => {
          const imagen = imagenPrincipal(linea.producto);
          return `
            <article class="sp-mini-carrito__linea">
              <span class="sp-mini-carrito__thumb">
                <img src="${escaparHtml(imagen.url)}" alt="${escaparHtml(
                  imagen.texto_alternativo ?? linea.producto.nombre,
                )}" loading="lazy" decoding="async" />
              </span>
              <div class="sp-mini-carrito__info">
                <strong class="sp-mini-carrito__nombre">${escaparHtml(linea.producto.nombre)}</strong>
                ${linea.varianteTexto ? `<small class="sp-mini-carrito__variante">${escaparHtml(linea.varianteTexto)}</small>` : ''}
                <div class="sp-mini-carrito__meta">
                  <span class="sp-mini-carrito__cantidad">Cantidad: ${linea.cantidad}</span>
                  <span class="sp-mini-carrito__precio">${formatearMoneda(Number(linea.precio), moneda)}</span>
                </div>
              </div>
            </article>
          `;
        })
        .join('')}
    </div>
    <a class="sp-boton sp-boton--primario sp-boton--ancho sp-mini-carrito__accion" href="/carrito">Revisar carrito</a>
  `;
}

export function footerHtml(tienda, estado) {
  const redes = enlacesRedes(tienda);
  const perfil = tienda.perfil ?? {};
  const mapa =
    perfil.mapa_url ||
    (perfil.direccion
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(perfil.direccion)}`
      : '');
  const horario = debeMostrarHorario(perfil) ? formatearHorario(perfil) : '';
  const ano = new Date().getFullYear();
  const variante = estado.editor?.footer?.variante ?? 'clasico';
  const minimal = variante === 'minimal';

  return `
    <footer class="sp-footer sp-footer--${variante}">
      <div class="sp-contenedor sp-footer__contenido">
        <div class="sp-footer__marca">
          <a class="sp-marca sp-marca--footer" href="/">
            ${marcaHtml(tienda, { paraFooter: true })}
            <span>${escaparHtml(tienda.empresa.nombre)}</span>
          </a>
          <p>${escaparHtml(
            perfil.descripcion ??
              'Tienda online preparada para atenderte rápido y con información clara.',
          )}</p>
        </div>
        ${
          minimal
            ? ''
            : `
        <div class="sp-footer__bloque">
          <h2>Contacto</h2>
          ${perfil.direccion ? `<p>${iconoLucide('MapPin')}${escaparHtml(perfil.direccion)}</p>` : ''}
          ${perfil.ciudad ? `<p>${iconoLucide('MapPin')}${escaparHtml(perfil.ciudad)}</p>` : ''}
          ${perfil.correo ? `<p>${iconoLucide('Mail')}${escaparHtml(perfil.correo)}</p>` : ''}
          ${perfil.whatsapp ? `<p>${iconoLucide('Phone')}${escaparHtml(perfil.whatsapp)}</p>` : ''}
          ${perfil.telefono && perfil.telefono !== perfil.whatsapp ? `<p>${iconoLucide('Phone')}${escaparHtml(perfil.telefono)}</p>` : ''}
          ${horario ? `<p>${iconoLucide('Clock')}${escaparHtml(horario)}</p>` : ''}
        </div>
        <div class="sp-footer__bloque">
          <h2>Redes</h2>
          ${
            redes.length
              ? `<div class="sp-redes sp-redes--iconos">${redes
                  .map(
                    ([clave, url, nombre]) => `
                      <a href="${escaparHtml(url)}" target="_blank" rel="noreferrer" aria-label="${escaparHtml(nombre)}">
                        ${iconoRedSocial(clave, { label: nombre })}
                      </a>`,
                  )
                  .join('')}</div>`
              : '<p>Sin redes publicadas.</p>'
          }
          ${mapa ? `<a class="sp-footer__mapa" href="${mapa}" target="_blank" rel="noreferrer">Ver mapa</a>` : ''}
        </div>
        `
        }
      </div>
      <div class="sp-contenedor sp-footer__legal">
        <span>© ${ano} ${escaparHtml(tienda.empresa.nombre)}. Todos los derechos reservados.</span>
        ${
          estado.branding
            ? `<a class="sp-footer__branding" href="https://vimosdigital.com" target="_blank" rel="noreferrer">
                Creado con <strong>VimosDigital</strong>
              </a>`
            : ''
        }
      </div>
    </footer>
  `;
}

export function avisoEstadoTiendaHtml(tienda, estado) {
  if (!estado.cerrada) return '';
  return `
    <div class="sp-estado-tienda sp-estado-tienda--cerrada" role="status">
      <div class="sp-contenedor">
        <span aria-hidden="true">${iconoLucide('Clock')}</span>
        <p>Esta tienda está cerrada temporalmente: podrás ver el catálogo, pero la venta está pausada.</p>
      </div>
    </div>
  `;
}

// ============================================================================
// Comportamiento de los drawers móviles (menú y carrito): apertura de la
// hamburguesa, cierre por X/overlay/Escape, bloqueo de scroll, foco inicial y
// trampa de Tab. VIVE AQUÍ porque es EL MISMO código que ejecuta el storefront
// (main.js) y el preview del panel (personalizar-tienda): un solo origen para
// la navegación móvil que verá la tienda publicada.
// ============================================================================

/**
 * Conecta la navegación móvil por drawers dentro de `doc`.
 * Idempotente respecto a su DOM: se puede llamar una vez por documento/marco.
 *
 * @param {{ doc?: Document, ventana?: Window }} [opciones]
 * @returns {{ cerrar: () => void }}
 */
export function conectarDrawersMoviles({ doc = document, ventana = window } = {}) {
  const overlay = doc.querySelector('[data-sp-overlay]');
  const menu = doc.querySelector('[data-sp-menu]');
  const carrito = doc.querySelector('[data-sp-carrito]');
  const abrirMenu = doc.querySelector('[data-sp-menu-abrir]');
  const cerrarMenu = doc.querySelector('[data-sp-menu-cerrar]');
  const abrirCarrito = doc.querySelector('[data-sp-carrito-abrir]');
  const cerrarCarrito = doc.querySelector('[data-sp-carrito-cerrar]');
  const body = doc.body;
  const enMovil = () => ventana.matchMedia('(max-width: 820px)').matches;
  let ultimoEnfoque = null;
  let drawerAbierto = null;

  const abrir = (drawer) => {
    if (!drawer) return;
    ultimoEnfoque = doc.activeElement;
    drawerAbierto = drawer;
    drawer.classList.add('sp-drawer--abierto');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.hidden = false;
    body.classList.add('sp-bloqueado');
    drawer.querySelector('[data-sp-menu-cerrar],[data-sp-carrito-cerrar]')?.focus();
  };

  const cerrar = () => {
    [menu, carrito].forEach((drawer) => {
      if (!drawer) return;
      drawer.classList.remove('sp-drawer--abierto');
      drawer.setAttribute('aria-hidden', 'true');
    });
    drawerAbierto = null;
    if (overlay) overlay.hidden = true;
    body.classList.remove('sp-bloqueado');
    if (ultimoEnfoque && ultimoEnfoque.isConnected) ultimoEnfoque.focus();
    ultimoEnfoque = null;
  };

  const recolectarEnfocables = (drawer) =>
    [
      ...drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => !el.closest('[aria-hidden="true"]'));

  abrirMenu?.addEventListener('click', () => abrir(menu));
  cerrarMenu?.addEventListener('click', cerrar);
  cerrarCarrito?.addEventListener('click', cerrar);
  overlay?.addEventListener('click', cerrar);

  abrirCarrito?.addEventListener('click', (evento) => {
    if (!enMovil()) return;
    evento.preventDefault();
    abrir(carrito);
  });

  [menu, carrito].forEach((drawer) => {
    drawer?.querySelectorAll('a').forEach((enlace) => enlace.addEventListener('click', cerrar));
  });

  ventana.addEventListener('keydown', (evento) => {
    if (evento.key === 'Escape') {
      cerrar();
      return;
    }
    if (evento.key !== 'Tab' || !drawerAbierto) return;
    const enfocables = recolectarEnfocables(drawerAbierto);
    if (enfocables.length === 0) return;
    const primero = enfocables[0];
    const ultimo = enfocables[enfocables.length - 1];
    if (evento.shiftKey && doc.activeElement === primero) {
      evento.preventDefault();
      ultimo.focus();
    } else if (evento.key === 'Tab' && doc.activeElement === ultimo) {
      evento.preventDefault();
      primero.focus();
    }
  });

  return { cerrar };
}

export function conectarBuscadorExpandible({ doc = document } = {}) {
  const expandible = doc.querySelector('[data-sp-busqueda-expandible]');
  if (!expandible) return;

  const abrir = () => {
    const panel = expandible.querySelector('#sp-busqueda-panel');
    const trigger = expandible.querySelector('[data-sp-busqueda-abrir]');
    panel?.removeAttribute('hidden');
    trigger?.setAttribute('aria-expanded', 'true');
    panel?.querySelector('input')?.focus();
  };

  const cerrarBusqueda = () => {
    const panel = expandible.querySelector('#sp-busqueda-panel');
    const trigger = expandible.querySelector('[data-sp-busqueda-abrir]');
    panel?.setAttribute('hidden', '');
    trigger?.setAttribute('aria-expanded', 'false');
  };

  expandible.querySelector('[data-sp-busqueda-abrir]')?.addEventListener('click', abrir);
  expandible.querySelector('[data-sp-busqueda-cerrar]')?.addEventListener('click', cerrarBusqueda);
}
